import { z } from "zod";

/**
 * The one calendar month a run pays for, as the shore insists on it.
 *
 * `assertOneCalendarMonth` refuses anything that is not "first day to last day
 * of the same month", and it refuses it AFTER the admin has filled the form in.
 * So the wizard never asks for two dates: it asks for a month, and derives both
 * ends here. A date picker would have let somebody pick the 15th and learn
 * about the rule from a 400.
 */

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

export const runCycleMonthSchema = z.string().regex(MONTH, "Pick the month this run pays for.");

/** `YYYY-MM` to the first day of that month. */
export function monthStart(month: string): string {
  return `${month}-01`;
}

/** `YYYY-MM` to the last day of that month, leap years included. */
export function monthEnd(month: string): string {
  const [yearPart, monthPart] = month.split("-");
  const year = Number(yearPart);
  const monthNumber = Number(monthPart);
  // Day 0 of the NEXT month is the last day of this one, and `Date.UTC` keeps
  // the arithmetic off the reader's timezone: a cycle is a calendar fact.
  const lastDay = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  return `${month}-${String(lastDay).padStart(2, "0")}`;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function monthLabel(month: string): string {
  const [yearPart, monthPart] = month.split("-");
  const name = MONTH_NAMES[Number(monthPart) - 1] ?? month;
  return `${name} ${yearPart}`;
}

/**
 * The months a run may plausibly be created for, newest first.
 *
 * Twelve back and one forward: payroll is run for the month that just ended,
 * occasionally for one that was missed, and sometimes prepared a month ahead.
 * A free date entry would mostly be a way to make typos, and the shore would
 * refuse anything outside a live payroll period anyway.
 */
export function recentCycleMonths(today: Date, back = 12, forward = 1): string[] {
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const months: string[] = [];
  for (let offset = forward; offset >= -back; offset -= 1) {
    const cursor = new Date(Date.UTC(year, month + offset, 1));
    const label = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, "0")}`;
    months.push(label);
  }
  return months;
}

/** The month a run created today most likely pays for: the one that just ended. */
export function defaultCycleMonth(today: Date): string {
  const previous = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 1, 1));
  return `${previous.getUTCFullYear()}-${String(previous.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Prefer a sendable suggestion when the admin has not picked a month yet. */
export function resolveSendMonth(input: {
  selected: string;
  touched: boolean;
  sendable: boolean;
  suggestedCycleStart: string | null;
}): string {
  if (input.touched || input.sendable || input.suggestedCycleStart === null) {
    return input.selected;
  }
  return input.suggestedCycleStart.slice(0, 7);
}
